import { Stack, Typography } from "@mui/material";

import React, { useState } from "react";
import CaptionAndFilter from "./component/CaptionAndFilter";
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
  AreaChart,
  Area,
} from "recharts";
import { useCustomQuery } from "store/hooks/react-query/query/useQuery";
import { VITAL_SIGNS_GRAPH } from "utils/reactQueryKeys";
import { useSanitizeQueryParams } from "utils/sanitizeQuery";
import { useParams } from "react-router-dom";
import CustomLoader from "components/atoms/CustomLoader";
import CustomGraphTooltip from "./component/CustomGraphTooltip";
import CustomRefLine from "./component/CustomRefLine";

const Graph = ({ data }) => {
  return (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={data?.graph ?? []}>
        <defs>
          <linearGradient id="gradientFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="10%" stopColor="rgba(244, 63, 94, 0.4)" />
            <stop offset="100%" stopColor="rgba(244, 63, 94, 0)" />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
        <XAxis dataKey="xName" />
        <YAxis
          unit={data?.range?.unit}
          domain={["auto", Number(data?.range?.normal?.max || 0) + 5]}
          allowDecimals
          tickCount={10}
        />
        <Tooltip content={<CustomGraphTooltip yName="Rate" />} />

        <ReferenceLine
          y={Number(data?.range?.normal?.max ?? 0)}
          label={
            <CustomRefLine
              range={`${data?.range?.normal?.min}${data?.range?.unit} - ${data?.range?.normal?.max}${data?.range?.unit}`}
            />
          }
          stroke="#00843566"
          strokeDasharray="5 5"
        />
        <Area
          type="monotone"
          dataKey="yValue"
          unit={data?.range?.unit}
          stroke="#F43F5E"
          strokeWidth={"3px"}
          fill="url(#gradientFill)"
          dot={false}
        />
        <ReferenceLine
          y={Number(data?.range?.normal?.min ?? 0)}
          label=""
          stroke="#00843566"
          strokeDasharray="5 5"
        />
      </AreaChart>
    </ResponsiveContainer>
  );
};
const VITALS_CHECKED = "heartRate";
function HeartRate() {
  const { id } = useParams();

  const [filter, setfilter] = useState({
    name: "This Month",
    value: "ThisMonth",
  });

  //get vital signs
  const {
    data: vitalsGraph,
    isLoading,
    isError,
  } = useCustomQuery(
    [
      VITAL_SIGNS_GRAPH,
      id,
      {
        timeType: filter.value,
        vitalSign: VITALS_CHECKED,
      },
    ],
    {
      url: useSanitizeQueryParams(
        `/visit/patients/${id}/vitals-graph?timeType=${filter.value}&vitalSign=${VITALS_CHECKED}`
      ),
      method: "get",
    },
    {
      refetchOnWindowFocus: false,
    }
  );

  const handleDropdown = (item) => {
    setfilter(item);
  };
  return (
    <Stack
      spacing={4}
      sx={{
        border: {
          lg: "1px solid rgba(196, 196, 196, 0.25)",
        },
        padding: {
          xs: 0,
          lg: 3,
        },
        borderRadius: {
          xs: "none",
          lg: "12px",
        },
      }}
    >
      <CaptionAndFilter
        filter={filter}
        handleDropdown={handleDropdown}
        icon={
          <svg
            width="28"
            height="26"
            viewBox="0 0 28 26"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M27.7072 6.35934C27.2409 4.53053 26.2469 2.95356 24.8327 1.79895C23.4551 0.674188 21.7675 0.0546875 20.0806 0.0546875C18.7874 0.0546875 17.5539 0.402883 16.4142 1.08948C15.5142 1.63188 14.7046 2.3695 13.9975 3.29022C12.3892 1.19394 10.2658 0.0546875 7.91719 0.0546875C6.23107 0.0546875 4.54407 0.674188 3.16665 1.79895C1.75293 2.95356 0.759146 4.53031 0.292825 6.35906C-0.0491904 7.70044 -0.20494 9.58289 0.469903 11.8468H4.70753L6.23768 8.78626C6.30581 8.65001 6.41052 8.53541 6.5401 8.45531C6.66967 8.37521 6.81898 8.33277 6.97132 8.33274C7.28211 8.33274 7.56604 8.50834 7.7049 8.78626L10.4854 14.3471L13.2659 8.78626C13.334 8.64998 13.4387 8.53536 13.5682 8.45525C13.6978 8.37515 13.8471 8.33272 13.9995 8.33274C14.3101 8.33274 14.594 8.50834 14.7331 8.78626L16.2632 11.8468H21.9766C22.4298 11.8468 22.7967 12.2141 22.7967 12.6669C22.7967 13.12 22.4298 13.4873 21.9766 13.4873H15.7563C15.4457 13.4873 15.1619 13.3117 15.0227 13.0337L13.9995 10.987L11.219 16.5478C11.1508 16.6841 11.0461 16.7986 10.9165 16.8787C10.787 16.9588 10.6377 17.0012 10.4854 17.0012C10.3331 17.0012 10.1838 16.9588 10.0542 16.8787C9.92463 16.7986 9.81992 16.6841 9.75179 16.5478L6.97148 10.987L5.94806 13.0339C5.87989 13.1701 5.77517 13.2847 5.64562 13.3647C5.51606 13.4448 5.36678 13.4872 5.21448 13.4873H1.08426C1.35836 14.0952 1.69332 14.7246 2.10003 15.3729C4.33281 18.9339 8.2037 22.2333 13.6045 25.1793C13.725 25.2451 13.86 25.2795 13.9972 25.2796C14.1345 25.2796 14.2695 25.2452 14.39 25.1795C19.7925 22.233 23.6645 18.9339 25.8983 15.3731C28.3118 11.5258 28.2142 8.34799 27.7072 6.35934Z"
              fill="#F43F5E"
            />
          </svg>
        }
        title={"Heart Rate"}
        subTitle={"Number of heartbeats per minute."}
      />
      <Stack
        sx={{
          width: {
            xs: "70vw",
            sm: "50vw",
          },
          height: {
            xs: "35vh",
            sm: "40vh",
            md: "70vh",
          },
        }}
      >
        {isLoading ? (
          <CustomLoader />
        ) : !isError ? (
          <Graph data={vitalsGraph.data} />
        ) : (
          "No data to display"
        )}
        {!isLoading && !isError && (
          <Typography
            sx={{
              fontWeight: "bold",
              fontSize: "14px",
              opacity: 0.7,
              textAlign: "center",
            }}
          >
            {filter.name}
          </Typography>
        )}
      </Stack>
    </Stack>
  );
}

export default HeartRate;
